







































import { Component, Vue, Prop, Emit } from 'vue-property-decorator'
import { CreditCardValue, CreditCardErrors } from './types'
import { CreditNumberInput, ExpirationInput, CvvInput } from './components'
@Component({
  components: {
    CreditNumberInput,
    ExpirationInput,
    CvvInput
  }
})
export default class CreditCardForm extends Vue {
  @Prop({ required: true, type: Object }) private value!: CreditCardValue
  @Prop({ default: false, type: Boolean }) private isConfirm!: boolean

  // 親から渡されたエラーとは別に、内部検証用の保持（リアクティブな既知キーで初期化）
  private errorsState: CreditCardErrors = {
    creditNumber: '',
    expirationMonth: '',
    expirationYear: '',
    expiration: '',
    securityCode: ''
  }

  // v-model 更新
  @Emit('input')
  private update(value: CreditCardValue) {
    return value
  }

  private normalizeDigits(v: string): string {
    return (v || '').replace(/\D+/g, '')
  }

  private formatCardNumberForDisplay(digitsOnly: string): string {
    return digitsOnly
      .replace(/\s+/g, '')
      .replace(/(\d{4})(?=\d)/g, '$1 ')
      .trim()
  }

  private get brandLabel(): string {
    const n = this.normalizeDigits(this.value.creditNumber)
    if (n.startsWith('4')) return 'VISA'
    if (n.startsWith('5')) return 'Mastercard'
    if (n.startsWith('3')) return 'AMEX'
    return ''
  }

  private get displayCreditNumber(): string {
    return this.formatCardNumberForDisplay(
      this.normalizeDigits(this.value.creditNumber)
    )
  }

  private get displaySecurityCode(): string {
    if (this.isConfirm) {
      return this.value.securityCode ? '•••' : ''
    }
    return this.value.securityCode
  }

  private onNumberInput(v: string) {
    const digits = this.normalizeDigits(v).slice(0, 16)
    const next: CreditCardValue = { ...this.value, creditNumber: digits }
    this.update(next)
    if (digits.length >= 14) this.$set(this.errorsState, 'creditNumber', '')
  }

  private onMonthInput(v: string) {
    const digits = this.normalizeDigits(v).slice(0, 2)
    const next: CreditCardValue = { ...this.value, expirationMonth: digits }
    this.update(next)
    if (digits.length === 2) {
      this.$set(this.errorsState, 'expirationMonth', '')
      const mm = Number(digits)
      if (mm >= 1 && mm <= 12) {
        ;(this.$refs.yy as HTMLInputElement)?.focus?.()
      }
    }
  }

  private onYearInput(v: string) {
    const digits = this.normalizeDigits(v).slice(0, 2)
    const next: CreditCardValue = { ...this.value, expirationYear: digits }
    this.update(next)
    if (digits.length === 2) this.$set(this.errorsState, 'expirationYear', '')
  }

  private onCvvInput(v: string) {
    const digits = this.normalizeDigits(v).slice(0, 4)
    const next: CreditCardValue = { ...this.value, securityCode: digits }
    this.update(next)
    if (digits.length >= 3) this.$set(this.errorsState, 'securityCode', '')
  }

  private validateNumber(): boolean {
    const digits = this.normalizeDigits(this.value.creditNumber)
    if (digits.length < 14 || digits.length > 16) {
      this.$set(
        this.errorsState,
        'creditNumber',
        '正しいクレジット番号を入力してください'
      )
      return false
    }
    this.$set(this.errorsState, 'creditNumber', '')
    return true
  }

  private validateMonth(): boolean {
    const mm = this.value.expirationMonth
    if (mm.length !== 2) {
      this.$set(
        this.errorsState,
        'expirationMonth',
        '正しい有効期限（月）を入力してください'
      )
      return false
    }
    const n = Number(mm)
    if (n < 1 || n > 12) {
      this.$set(
        this.errorsState,
        'expirationMonth',
        '正しい有効期限（月）を入力してください'
      )
      return false
    }
    this.$set(this.errorsState, 'expirationMonth', '')
    return true
  }

  private validateYear(): boolean {
    const yy = this.value.expirationYear
    if (yy.length !== 2) {
      this.$set(
        this.errorsState,
        'expirationYear',
        '正しい有効期限（年）を入力してください'
      )
      return false
    }
    this.$set(this.errorsState, 'expirationYear', '')
    return true
  }

  private validateExpiryNotPast(): boolean {
    const mmOk = this.validateMonth()
    const yyOk = this.validateYear()
    if (!mmOk || !yyOk) return false
    const mm = Number(this.value.expirationMonth)
    const yy = Number(this.value.expirationYear)
    const now = new Date()
    const currentYY = now.getFullYear() % 100
    const currentMM = now.getMonth() + 1
    if (yy < currentYY || (yy === currentYY && mm < currentMM)) {
      this.$set(this.errorsState, 'expiration', '有効期限が過去です')
      return false
    }
    this.$set(this.errorsState, 'expiration', '')
    return true
  }

  private validateCvv(): boolean {
    const digits = this.normalizeDigits(this.value.securityCode)
    if (digits.length < 3 || digits.length > 4) {
      this.$set(
        this.errorsState,
        'securityCode',
        '正しいセキュリティコードを入力してください'
      )
      return false
    }
    this.$set(this.errorsState, 'securityCode', '')
    return true
  }

  public validate(): boolean {
    this.errorsState = {
      creditNumber: '',
      expirationMonth: '',
      expirationYear: '',
      expiration: '',
      securityCode: ''
    }
    const okNumber = this.validateNumber()
    const okMonth = this.validateMonth()
    const okYear = this.validateYear()
    const okExp = okMonth && okYear ? this.validateExpiryNotPast() : false
    const okCvv = this.validateCvv()
    const ok = okNumber && okMonth && okYear && okExp && okCvv
    if (!ok) {
      this.$nextTick(() => {
        const firstErrorEl = this.$el.querySelector(
          '.ccform__input--error'
        ) as HTMLElement
        if (firstErrorEl)
          firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    }
    return ok
  }

  public getErrors(): CreditCardErrors {
    return { ...this.errorsState }
  }
}
